import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Trophy,
  Users,
  Heart,
  ArrowRight,
  CheckCircle2,
  Clock,
  GraduationCap,
  Shield,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { CALENDLY_URL } from '../lib/siteConfig';
import Footer from '../components/Footer';
import CallFrontDeskLine from '../components/CallFrontDeskLine';

/**
 * Public /workshops marketing page.
 *
 * Showcases a single preview slide per audience (athlete / parent / coach)
 * along with format, duration, and outcomes. The full decks themselves are
 * private sales collateral and live behind admin auth at
 *   /api/admin/workshop-decks/{deck_id}
 * \u2014 we never link or expose them on this page.
 *
 * Single CTA on the page: "Book a Planning Call" -> CALENDLY_URL.
 */

const fadeIn = {
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-50px' },
  transition: { duration: 0.5 },
};

const WORKSHOPS = [
  {
    slug: 'athlete',
    audience: 'For Athletes',
    title: 'Training the Mind to Win',
    subtitle:
      'A live mental-performance reset for middle school, high school, and college athletes.',
    icon: Trophy,
    preview: '/workshop-preview-athlete.jpg',
    format: '60 \u2013 90 minutes \u00b7 in-person',
    audienceSize: '12 \u2013 60 athletes \u00b7 single team or full program',
    bestFor:
      'Pre-season install, mid-season reset, or post-loss reset for a roster that\u2019s mentally drifting.',
    outcomes: [
      'A shared mental-performance vocabulary the whole roster uses by Monday',
      'Two personal tools each athlete walks out with and can run in 30 seconds',
      'A live demonstration of the Apex Six in practice \u2014 not theory',
      'A printed Game Plan worksheet every athlete leaves with',
    ],
  },
  {
    slug: 'parent',
    audience: 'For Parents',
    title: 'Raising a Confident Athlete',
    subtitle:
      'A live workshop for parent groups, booster clubs, and team parent meetings.',
    icon: Heart,
    preview: '/workshop-preview-parent.jpg',
    format: '60 \u2013 90 minutes \u00b7 in-person or virtual',
    audienceSize: '15 \u2013 100 parents \u00b7 team, club, or PTA-style gathering',
    bestFor:
      'Parent groups that want to support their athlete without adding more pressure.',
    outcomes: [
      "A simple postgame script every parent can run \u2014 no more car-ride disasters",
      "The difference between words that build vs. words that break, with real swaps",
      "A 3-step regulation tool parents can use BEFORE they speak to their athlete",
      "One concrete commitment each parent leaves the room with",
    ],
  },
  {
    slug: 'coach',
    audience: 'For Coaches & Athletic Directors',
    title: 'Mental Performance for Coaches',
    subtitle:
      'A program-level install for coaching staffs and full athletic departments.',
    icon: Users,
    preview: '/workshop-preview-coach.jpg',
    format: '90 minutes to a full coaching clinic \u00b7 in-person',
    audienceSize: 'Coaching staffs, single sport up to whole athletic department',
    bestFor:
      'Programs ready to install a repeatable mental-performance framework instead of one-off speakers.',
    outcomes: [
      'A coach-deliverable version of the Apex Six \u2014 your staff runs the tools, not me',
      "A pre-season, in-season, and post-season rollout plan tailored to your sport",
      "Language scripts for the first 10 seconds after a mistake \u2014 the moments that matter most",
      'A daily 30-second athlete check-in system every coach can run',
    ],
  },
];

export default function Workshops() {
  useEffect(() => {
    document.title = 'In-Person Workshops \u2014 Apex Athletic Mind';
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <main data-testid="workshops-page">
        {/* ---- Hero ---- */}
        <section className="relative overflow-hidden bg-gradient-to-b from-[#004C54] to-[#003a40] text-white pt-20 pb-16 sm:pt-28 sm:pb-20">
          <div className="max-w-[1100px] mx-auto px-5 sm:px-6">
            <motion.div {...fadeIn}>
              <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-3 py-1.5 text-[11px] font-mono uppercase tracking-[0.2em] text-[#4FBCC7] font-bold mb-6">
                <Shield className="h-3.5 w-3.5" /> In-Person · Frameworks-Based · Tri-State · Travel by Quote
              </div>
              <h1
                className="font-heading text-4xl sm:text-5xl lg:text-[58px] font-bold tracking-tight leading-[1.05]"
                data-testid="workshops-hero-headline"
              >
                Live workshops for the people
                <br className="hidden sm:block" />
                <span className="text-[#4FBCC7]"> who shape the athlete.</span>
              </h1>
              <p className="mt-6 text-lg sm:text-xl text-white/80 leading-relaxed max-w-[64ch]">
                Coach Varsay Lewis runs three distinct in-person workshops &mdash; one for
                athletes, one for parents, one for coaches and athletic directors &mdash;
                built on the same Apex Six frameworks behind a Non-Public B State
                Championship program. Each one is interactive, customized to your team or
                program, and designed to install real tools, not deliver a one-off speech.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Button
                  asChild
                  size="lg"
                  className="h-12 px-7 rounded-full bg-[#4FBCC7] hover:bg-[#3FA8B3] text-[#003a40] font-bold tracking-tight"
                >
                  <a
                    href={CALENDLY_URL}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="workshops-hero-cta"
                  >
                    Book a Planning Call <ArrowRight className="ml-2 h-4 w-4" />
                  </a>
                </Button>
                <a
                  href="#workshop-list"
                  className="inline-flex h-12 items-center justify-center rounded-full border border-white/30 px-6 text-sm font-semibold text-white/90 hover:bg-white/10 transition-colors"
                  data-testid="workshops-hero-secondary"
                >
                  See the three workshops
                </a>
              </div>
              <div className="mt-4">
                <CallFrontDeskLine tone="dark" align="left" testId="workshops-hero-call-line" />
              </div>
            </motion.div>
          </div>
        </section>

        {/* ---- Workshop cards ---- */}
        <section id="workshop-list" className="py-16 sm:py-24">
          <div className="max-w-[1100px] mx-auto px-5 sm:px-6 space-y-12">
            {WORKSHOPS.map((w, idx) => {
              const Icon = w.icon;
              const reverse = idx % 2 === 1;
              return (
                <motion.div
                  key={w.slug}
                  {...fadeIn}
                  className="bg-white rounded-2xl border border-[#004C54]/10 shadow-[0_10px_30px_rgba(0,76,84,0.08)] overflow-hidden"
                  data-testid={`workshop-card-${w.slug}`}
                >
                  <div
                    className={`grid lg:grid-cols-[1.1fr_0.9fr] gap-0 ${
                      reverse ? 'lg:[&>:first-child]:order-2' : ''
                    }`}
                  >
                    {/* --- Preview slide image --- */}
                    <div className="bg-[#0a0d0e] p-6 sm:p-8 flex items-center justify-center">
                      <div className="w-full max-w-[560px] rounded-lg overflow-hidden border border-white/10 shadow-2xl">
                        <img
                          src={w.preview}
                          alt={`${w.title} \u2014 sample slide preview`}
                          className="w-full h-auto block"
                          data-testid={`workshop-preview-img-${w.slug}`}
                          loading="lazy"
                        />
                        <div className="bg-[#004C54] text-white/70 text-[11px] uppercase tracking-[0.22em] font-mono-accent font-bold px-4 py-2.5 text-center">
                          One sample slide · full deck delivered live by Coach Lewis
                        </div>
                      </div>
                    </div>

                    {/* --- Copy --- */}
                    <div className="p-7 sm:p-10">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-11 h-11 rounded-xl bg-[#004C54] flex items-center justify-center">
                          <Icon className="h-5 w-5 text-white" aria-hidden="true" />
                        </div>
                        <div className="font-mono-accent uppercase tracking-[0.22em] text-[10.5px] text-[#004C54] font-bold">
                          {w.audience}
                        </div>
                      </div>

                      <h2 className="font-heading text-[1.7rem] sm:text-[2rem] font-bold tracking-tight leading-[1.15] text-[#0a0d0e]">
                        {w.title}
                      </h2>
                      <p className="mt-3 text-[15px] sm:text-[16px] text-[#3a3a3a] leading-relaxed">
                        {w.subtitle}
                      </p>

                      <dl className="mt-5 space-y-2.5 text-[13.5px]">
                        <div className="flex items-start gap-2.5">
                          <Clock className="h-4 w-4 text-[#004C54] shrink-0 mt-0.5" aria-hidden="true" />
                          <span className="text-[#516570]">
                            <b className="text-[#0a0d0e]">Format: </b>{w.format}
                          </span>
                        </div>
                        <div className="flex items-start gap-2.5">
                          <Users className="h-4 w-4 text-[#004C54] shrink-0 mt-0.5" aria-hidden="true" />
                          <span className="text-[#516570]">
                            <b className="text-[#0a0d0e]">Audience: </b>{w.audienceSize}
                          </span>
                        </div>
                        <div className="flex items-start gap-2.5">
                          <GraduationCap className="h-4 w-4 text-[#004C54] shrink-0 mt-0.5" aria-hidden="true" />
                          <span className="text-[#516570]">
                            <b className="text-[#0a0d0e]">Best for: </b>{w.bestFor}
                          </span>
                        </div>
                      </dl>

                      <div className="mt-6 border-t border-[#004C54]/10 pt-5">
                        <div className="font-mono-accent uppercase tracking-[0.22em] text-[10.5px] text-[#004C54] font-bold mb-3">
                          What you walk away with
                        </div>
                        <ul className="space-y-2.5">
                          {w.outcomes.map((o) => (
                            <li
                              key={o}
                              className="flex items-start gap-2.5 text-[14.5px] leading-[1.55] text-[#0a0d0e]"
                            >
                              <CheckCircle2 className="h-[18px] w-[18px] text-[#4FBCC7] shrink-0 mt-0.5" aria-hidden="true" />
                              <span className="flex-1">{o}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* ---- Closing CTA band ---- */}
        <section className="bg-[#004C54] text-white py-16 sm:py-20">
          <div className="max-w-[860px] mx-auto px-5 sm:px-6 text-center">
            <motion.div {...fadeIn}>
              <div className="font-mono-accent uppercase tracking-[0.22em] text-[10.5px] text-[#4FBCC7] font-bold mb-4">
                Custom by sport, age, and program
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-[2.6rem] font-bold tracking-tight leading-[1.15]">
                Let&rsquo;s design the right workshop
                <br className="hidden sm:block" /> for your team.
              </h2>
              <p className="mt-5 text-base sm:text-lg text-white/80 leading-relaxed max-w-[58ch] mx-auto">
                Every workshop is tailored to your program &mdash; sport, age, season,
                and the specific mental-game gaps you&rsquo;re seeing. The Planning Call
                is 30 minutes, no pressure, designed to map fit before pricing.
              </p>
              <div className="mt-8">
                <Button
                  asChild
                  size="lg"
                  className="h-12 px-8 rounded-full bg-[#4FBCC7] hover:bg-[#3FA8B3] text-[#003a40] font-bold tracking-tight"
                >
                  <a
                    href={CALENDLY_URL}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="workshops-footer-cta"
                  >
                    Book a Planning Call <ArrowRight className="ml-2 h-4 w-4" />
                  </a>
                </Button>
                <p className="mt-3 text-[12px] text-white/55">
                  30-minute call · no charge · schools, clubs, travel programs welcome
                </p>
                <div className="mt-3">
                  <CallFrontDeskLine tone="dark" testId="workshops-footer-call-line" />
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
